"use client";

import { useCallback, useState } from "react";
import { AxiosResponse } from "axios";

import { getAuthCookie } from "@/services/CookiesService";
import { isForceLogoutError } from "@/utils/apiErrorHandler";

import { UseApiOptions, UseApiDependencies, UseApiResult } from "./types";

export const useApi = <T>(
  apiFunc: (data?: any) => Promise<AxiosResponse<T>>,
  options: UseApiOptions = {},
  dependencies: UseApiDependencies = {}
): UseApiResult<T> => {
  const { disableLoading = false, onSuccess, onError } = options;

  const { loadingManager, authProvider = getAuthCookie, errorHandler } = dependencies;

  const [loading, setLoading] = useState<boolean>(false);

  const updateLoading = useCallback(
    (newLoading: boolean) => {
      if (!disableLoading) {
        setLoading(newLoading);
        loadingManager?.setLoading(newLoading);
      }
    },
    [disableLoading, loadingManager]
  );

  const request = useCallback(
    async (requestData?: any): Promise<AxiosResponse<T>> => {
      updateLoading(true);

      try {
        const result = await apiFunc({
          authorization: authProvider() || requestData?.authorization,
          ...requestData,
        });

        onSuccess?.(result);
        updateLoading(false);

        return result;
      } catch (err) {
        if (isForceLogoutError(err)) {
          updateLoading(false);
          return new Promise<AxiosResponse<T>>(() => {});
        }

        errorHandler?.(err);
        onError?.(err);

        updateLoading(false);
        throw err;
      }
    },
    [apiFunc, authProvider, onSuccess, onError, errorHandler, updateLoading]
  );

  return {
    request,
    loading,
  };
};
